import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { update } from '../../Actions/Cart.action';
import { OrderRow } from '../../Types/OrderRow';
import QuantityInput from '../QuantityInput/QuantityInput';
import clsx from 'clsx';
import Icon from '../Icon';
import { translate } from '../../Services/translation';
import Input from '../Inputs/Input';
import { useDebouncedCallback } from 'use-debounce';
import StockStatusPanel from '../ProductCards/DetailedProductCardComponents/StockStatusPanel';
import StockStatus from '../StockStatus/StockStatus';

interface OrderRowProductCardProps {
  row: OrderRow;
  onRemoveClick?: (row: OrderRow) => void;
  className?: string;
}

const OrderRowProductCardB2B = ({
  row,
  onRemoveClick,
  className = '',
}: OrderRowProductCardProps) => {
  const dispatch = useDispatch();

  const [abortController, setAbortController] = useState<
    AbortController | undefined
  >();

  const [batchOpen, setBatchOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState<boolean>(!!row.note);
  const [note, setNote] = useState<string>(row.note ?? '');
  const [isRowValid, setIsRowValid] = useState(true);

  useEffect(() => {
    if (!noteOpen && note) {
      setNote('');
      dispatch(update(row.rowSystemId, row.quantity, ''));
    }
  }, [noteOpen, setNote]);

  useEffect(() => setAbortController(new AbortController()), []);

  const onQuantityChange = useCallback(
    (row: OrderRow, quantity: number) => {
      dispatch(update(row.rowSystemId, quantity, note, abortController));
    },
    [abortController, dispatch, note]
  );

  const debouncedSetNote = useDebouncedCallback((value) => {
    if (value && value !== '') {
      setNote(value);
    }
  }, 300);

  useEffect(() => {
    if (note) {
      noteOpen &&
        dispatch(
          update(row.rowSystemId, row.quantity, note, abortController)
        );
    }
  }, [note, dispatch, noteOpen]);

  useEffect(() => {
    const isValid =
      row.itemBatchInventories !== null &&
      !/^[^\d]/.test(row.itemBatchInventories?.trim() ?? '');
    setIsRowValid(isValid);
  }, [row.itemBatchInventories]);

  return (
    <div className={clsx(className, 'flex gap-x-4')}>
      <div className="flex w-full gap-4 border-b border-solid border-gray-100 pb-5">
        <div className="w-[100px]">
          {row.image && (
            <a href={row.url}>
              <img
                className="w-full rounded object-cover aspect-[3/4]"
                src={row.image}
              />
            </a>
          )}
        </div>
        <div className="flex flex-1 flex-col">
          <div className="flex flex-1 flex-col">
            <div className="mb-2 flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
              <span className="text-md leading-tight text-gray-500">
                {row.productType}
                {row.showArticleNumber && <>: {row.articleNumber}</>}
              </span>
              {row.stockStatus && (
                <StockStatus {...row.stockStatus} className="!mb-0" />
              )}
            </div>
            <h4 className="mb-1.5 text-h4">
              <a href={row.url}>{row.name}</a>
            </h4>
            <div className="mb-[6px] flex flex-col gap-y-[6px]">
              {row.extraInfo.items.map((item, _) => (
                <div
                  key={row.rowSystemId + '-extra-' + item.key}
                  className="text-md font-normal leading-tight text-gray-500"
                >
                  {item.key}: {item.value}
                </div>
              ))}
              {row.previewUrl && (
                <a
                  className="text-md font-normal leading-tight text-gray-500 underline decoration-gray-500"
                  href={row.previewUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {translate('order-row.see-preview')}
                </a>
              )}
            </div>
            <div className="mb-2 flex flex-col text-md font-normal leading-tight text-gray-500 lg:block">
              {row.formattedUnitCampaignPrice ?? row.formattedUnitPrice}
              {row.unitOfMeasurement &&
                ' / ' + row.unitOfMeasurement.toLowerCase()}
              {row.campaignPrice && (
                <span className="mt-2 text-sm font-light text-gray-400 lg:ml-2 lg:mt-0">
                  {translate('cart-item.regular-unit-price')}{' '}
                  {row.formattedUnitPrice}
                </span>
              )}
            </div>
            <div className="mb-4 flex gap-4">
              <div className="flex items-center gap-1">
                <p className="text-md font-normal text-gray-700">
                  {row.stockStatus?.inStock &&
                  row.stockStatus.quantity == 0 ? (
                    row.stockStatus.description
                  ) : (
                    <>
                      {row.stockStatus?.quantity ?? '0'}{' '}
                      {translate('cart-item.amount-in-stock')}
                    </>
                  )}
                </p>
                {isRowValid && (
                  <button
                    onClick={() => setBatchOpen(!batchOpen)}
                    className="flex cursor-pointer items-center"
                    aria-label="Open batch information"
                  >
                    <Icon
                      className="h-6 w-6 text-gray-700"
                      name="question-light"
                    />
                  </button>
                )}
              </div>
              <a
                onClick={() => setNoteOpen(!noteOpen)}
                className="flex cursor-pointer items-center"
              >
                <p className="text-md font-normal text-gray-700">
                  {translate(
                    noteOpen
                      ? 'cart-item.remove-note'
                      : 'cart-item.add-note'
                  )}
                </p>
                <Icon
                  className="h-6 w-6 pl-1 text-gray-700"
                  name={!noteOpen ? 'circle-plus' : 'circle-close'}
                />
              </a>
            </div>
            {batchOpen && (
              <div className="mb-3 rounded border border-gray-200 p-4">
                <StockStatusPanel
                  itemBatchInventories={row.itemBatchInventories}
                  productId={row.id}
                />
              </div>
            )}
            {noteOpen && (
              <Input
                className="mb-3"
                label={translate('cart-item.note')}
                onChange={(event) => debouncedSetNote(event.target.value)}
                defaultValue={note}
                showAnimatedLabel={true}
              />
            )}
          </div>
          <div className="flex items-center justify-between">
            <div className="flex flex-wrap items-center">
              <QuantityInput
                quantity={row.quantity}
                showButtons={!row.isFreeGift}
                disabled={row.isFreeGift || !row.canEditQuantity}
                mediumSize={true}
                wrapperClassName="max-w-[112px]"
                onChange={(quantity) => onQuantityChange(row, quantity)}
              />
              {onRemoveClick && !row.isFreeGift && (
                <button
                  className="color-gray-500 ml-2 cursor-pointer p-2"
                  onClick={() => onRemoveClick && onRemoveClick(row)}
                >
                  <Icon name="delete" className="h-5 w-5" />
                </button>
              )}
            </div>
            <div className="text-right text-lg font-normal">
              {row.campaignPrice && row.campaignPrice != ''
                ? row.campaignPrice
                : row.price}
              {row.campaignPrice && row.campaignPrice != '' && (
                <div className="text-sm text-gray-500 line-through">
                  {row.price}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderRowProductCardB2B;
